import { useCallback, useEffect, useRef, useState } from 'react'
import { Avatar } from '../../../components/Avatar'
import { PageHeader } from '../../../components/PageHeader'
import { RoomQR } from '../../../components/RoomQR'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useAuth } from '../../../lib/auth'
import { readError } from '../../../lib/errors'
import { useProfileAvatars } from '../../../lib/useProfileAvatars'
import {
  buildPromptText,
} from '../api/weekly'
import {
  addThreeStepAi, advanceThreeStepReveal, advanceThreeStepRound, stepBackThreeStepReveal,
  castThreeStepRoundVote, castThreeStepRoundVoteAsAi,
  fetchThreeStepAiPersonas, fetchThreeStepRoomSnapshot,
  generateAiActions, generateAiSteps,
  kickThreeStepPlayer,
  leaveThreeStepRoom, removeThreeStepAi, renameThreeStepSeat,
  startThreeStepGame, submitThreeStepActions, submitThreeStepActionsAsAi,
  submitThreeStepRoundSteps, submitThreeStepRoundStepsAsAi,
  subscribeToThreeStepRoom,
  type ThreeStepAiPersonaRow, type ThreeStepRoomSnapshot,
} from '../api/rooms'
import {
  aiPickActions, aiPickVoteTarget, aiWriteSteps,
} from '../ai/personas'
import { useT } from '../../../i18n'

interface Props {
  roomId: string
  onExit: () => void
}

const MAX_ACTION_LEN = 160
const MAX_STEP_LEN = 160

/**
 * Single-room orchestrator for live multiplayer Three Step Solution.
 *
 * Subscribes to all five tables for the room and re-fetches a full
 * snapshot on every event. The rendered sub-screen is picked from
 * `room.status`. Every player on their own device — no phone passing,
 * no shared screen, no host driving turns. Each player only ever sees
 * their own private input fields during the writing phases.
 *
 * AI autoplay: whoever is connected (any human in the room, including
 * the host) runs the autoplay loop. When an AI seat hasn't acted in
 * the current phase yet, the loop fires the matching `*_as_ai` RPC
 * with content generated client-side by `ai/personas.ts`. A small
 * delay + busy ref prevents stampedes when the realtime tick comes
 * back before the previous insert resolves.
 */
export function ThreeStepRoomScreen({ roomId, onExit }: Props) {
  const t = useT()
  const { profile } = useAuth()
  const [snap, setSnap] = useState<ThreeStepRoomSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [aiPersonas, setAiPersonas] = useState<ThreeStepAiPersonaRow[]>([])
  const [busy, setBusy] = useState(false)
  const cancelledRef = useRef(false)
  const autoplayBusyRef = useRef(false)
  // Per-session memory of which AI submissions THIS client has already
  // fired. Defense against the "host can't see AI actions through RLS
  // during phase1/3" issue: snapshot.actions is empty for AI authors,
  // so naively re-checking on every tick would re-submit forever and
  // reshuffle the prompt (the slot-machine bug). Cleared between rooms
  // by the cancelledRef guard on the snapshot effect. Key format:
  // `${phase}-${round}-${aiPlayerId}`.
  const submittedAiKeysRef = useRef<Set<string>>(new Set())

  // ── Snapshot + realtime subscription ────────────────────────────────
  useEffect(() => {
    cancelledRef.current = false
    function refetch() {
      fetchThreeStepRoomSnapshot(roomId)
        .then(s => { if (!cancelledRef.current) setSnap(s) })
        .catch(e => { if (!cancelledRef.current) setError(readError(e)) })
    }
    refetch()
    const unsub = subscribeToThreeStepRoom(roomId, refetch)
    return () => { cancelledRef.current = true; unsub() }
  }, [roomId])

  // During phase1 / phase3 / voting, other players' actions, steps and
  // votes are RLS-hidden, so realtime never fires for them on this
  // client. Poll the snapshot (which carries server-computed progress
  // counts) so the live "X/N submitted" stays current and the room
  // advances on time even if our realtime missed the status flip.
  const pollStatus = snap?.room.status
  useEffect(() => {
    if (pollStatus !== 'phase1' && pollStatus !== 'phase3' && pollStatus !== 'phase4_voting') return
    const id = setInterval(() => {
      fetchThreeStepRoomSnapshot(roomId)
        .then(s => { if (!cancelledRef.current) setSnap(s) })
        .catch(() => {})
    }, 3000)
    return () => clearInterval(id)
  }, [pollStatus, roomId])

  // AI persona pool (for the host's add-AI picker in lobby).
  useEffect(() => {
    fetchThreeStepAiPersonas()
      .then(rows => { if (!cancelledRef.current) setAiPersonas(rows) })
      .catch(() => {})
  }, [])

  // ── AI autoplay loop ────────────────────────────────────────────────
  // Whoever is connected (any human in the room) runs this on every
  // snapshot tick. For each AI seat that hasn't acted in the current
  // phase, generate content client-side and fire the matching `*_as_ai`
  // RPC. We process ALL pending AIs in this single call rather than
  // one-per-tick — the snapshot won't refresh between AI submissions
  // because RLS hides each player's actions/steps from other players
  // during phase1/phase3, so the realtime events for AI inserts don't
  // reach the human's client and we'd stall after the first AI.
  // Sequential, with a small jitter, to keep the order natural and
  // the server load steady.
  const runAiAutoplay = useCallback(async (snapshot: ThreeStepRoomSnapshot) => {
    if (autoplayBusyRef.current) return
    if (!profile) return
    const { room, players, actions, steps, votes } = snapshot

    // Only humans drive AIs.
    const meIsHuman = players.some(p => p.profile_id === profile.id)
    if (!meIsHuman) return

    const aiPlayers = players.filter(p => p.is_ai && !p.skipped)
    if (aiPlayers.length === 0) return

    autoplayBusyRef.current = true
    const submittedKeys = submittedAiKeysRef.current
    try {
      if (room.status === 'phase1') {
        for (const ai of aiPlayers) {
          const key = `phase1-${ai.id}`
          if (submittedKeys.has(key)) continue
          const mine = actions.filter(a => a.player_id === ai.id)
          const filled = mine.filter(a => a.text.trim().length > 0).length
          if (filled >= 3) { submittedKeys.add(key); continue }
          try {
            // Prefer Claude (fresh, in-character actions per game).
            // Fall back to local templates if the edge function 500s
            // or Anthropic is having a moment — game never stalls.
            const fromLlm = await generateAiActions(ai.id)
            const [a, b, c] = fromLlm ?? aiPickActions(ai.display_name)
            await submitThreeStepActionsAsAi(ai.id, a, b, c)
            submittedKeys.add(key)
          } catch (e) {
            console.warn('[3step ai] phase1 fail', ai.display_name, e)
          }
          // Tiny jitter between bots so the inserts read as separate
          // actors arriving at slightly different times. Claude calls
          // already take ~1-2s each so there's natural spacing.
          await new Promise(r => setTimeout(r, 100))
        }
      } else if (room.status === 'phase3') {
        const round = room.current_round
        const a = room.current_action_a, b = room.current_action_b
        if (a && b) {
          for (const ai of aiPlayers) {
            const key = `phase3-${round}-${ai.id}`
            if (submittedKeys.has(key)) continue
            const mine = steps.find(s => s.player_id === ai.id && s.round === round)
            if (mine && mine.submitted_at) { submittedKeys.add(key); continue }
            try {
              const fromLlm = await generateAiSteps(ai.id, a, b)
              const [s1, s2, s3] = fromLlm ?? aiWriteSteps(ai.display_name, a, b)
              await submitThreeStepRoundStepsAsAi(ai.id, s1, s2, s3)
              submittedKeys.add(key)
            } catch (e) {
              console.warn('[3step ai] phase3 fail', ai.display_name, e)
            }
            await new Promise(r => setTimeout(r, 100))
          }
        }
      } else if (room.status === 'phase4_voting') {
        const round = room.current_round
        const allIds = players.filter(p => !p.skipped).map(p => p.id)
        for (const ai of aiPlayers) {
          // Same once-per-round guard as phase1/phase3 — without it every
          // realtime tick between our cast and its echo re-fired the vote,
          // and each present human could flip the AI's target repeatedly.
          const key = `vote-${round}-${ai.id}`
          if (submittedKeys.has(key)) continue
          const already = votes.some(v => v.round === round && v.voter_id === ai.id)
          if (already) { submittedKeys.add(key); continue }
          try {
            const target = aiPickVoteTarget(ai.id, allIds)
            if (target) {
              await castThreeStepRoundVoteAsAi(ai.id, target)
              submittedKeys.add(key)
            }
          } catch (e) {
            console.warn('[3step ai] vote fail', ai.display_name, e)
          }
          await new Promise(r => setTimeout(r, 200))
        }
      }
    } finally {
      autoplayBusyRef.current = false
    }
  }, [profile])

  useEffect(() => {
    if (!snap) return
    // Light delay so multiple AIs don't all stampede on the same tick.
    const t = setTimeout(() => runAiAutoplay(snap), 350)
    return () => clearTimeout(t)
  }, [snap, runAiAutoplay])

  // ── Loading + error gates ───────────────────────────────────────────
  if (!snap) {
    return (
      <Shell title={t('Loading…')} onBack={onExit}>
        <div className="px-5 py-8 space-y-3">
          <div className="h-32 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-32 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
        {error && (
          <div className="px-5 mt-3">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}
      </Shell>
    )
  }

  const myPlayer = snap.players.find(p => p.profile_id === profile?.id) ?? null
  const isHost = profile?.id === snap.room.host_id

  // ── Phase router ────────────────────────────────────────────────────
  const status = snap.room.status

  if (status === 'lobby') {
    return (
      <LobbyView
        snap={snap}
        isHost={isHost}
        myPlayerId={myPlayer?.id ?? null}
        aiPersonas={aiPersonas}
        busy={busy}
        setBusy={setBusy}
        error={error}
        setError={setError}
        // Back button: just navigate away. The room stays in lobby
        // so the host can come back to it (or friends can still join
        // with the code). Matches the Colorful behaviour.
        onExit={onExit}
        // Explicit Leave button: tear down the seat + exit. If the
        // host leaves the lobby is empty, the leave RPC marks the
        // room abandoned. This is the only path that should abandon
        // a freshly-created lobby — the back button MUST NOT.
        onLeave={async () => {
          try { await leaveThreeStepRoom(roomId) } catch {}
          onExit()
        }}
      />
    )
  }

  if (status === 'phase1') {
    return (
      <Phase1View
        snap={snap}
        myPlayerId={myPlayer?.id ?? null}
        roomId={roomId}
        onExit={onExit}
      />
    )
  }

  if (status === 'phase3') {
    return (
      <Phase3View
        snap={snap}
        myPlayerId={myPlayer?.id ?? null}
        roomId={roomId}
        isHost={isHost}
        onExit={onExit}
      />
    )
  }

  if (status === 'phase4_reveal') {
    return <RevealView snap={snap} roomId={roomId} isHost={isHost} onExit={onExit} />
  }

  if (status === 'phase4_voting') {
    return (
      <VotingView
        snap={snap}
        myPlayerId={myPlayer?.id ?? null}
        roomId={roomId}
        isHost={isHost}
        onExit={onExit}
      />
    )
  }

  if (status === 'between_rounds') {
    return <BetweenRoundsView snap={snap} roomId={roomId} isHost={isHost} onExit={onExit} />
  }

  if (status === 'results' || status === 'abandoned') {
    return <ResultsView snap={snap} myPlayerId={myPlayer?.id ?? null} onExit={onExit} />
  }

  return null
}

// ────────────────────────────────────────────────────────────────────
// Shell — common chrome
// ────────────────────────────────────────────────────────────────────

function Shell({
  title, subtitle, code, onBack, children,
}: {
  title: string
  subtitle?: string
  /** Room code chip in the header — so a dropped player always knows
   *  what to re-enter to rejoin. */
  code?: string
  onBack: () => void
  children: React.ReactNode
}) {
  return (
    <div className="screen">
      <BackgroundTitle text="Three Step Solution" color="#0EAE93" />
      <PageHeader
        title={title}
        subtitle={subtitle}
        onBack={onBack}
        right={code ? (
          <span className="px-2.5 py-1 rounded-lg bg-brand-elevated border border-brand-border
                           text-xs font-bold tracking-[0.2em] text-brand-text whitespace-nowrap">
            {code}
          </span>
        ) : undefined}
      />
      {children}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Lobby — players join + host adds AIs / starts
// ────────────────────────────────────────────────────────────────────

function LobbyView({
  snap, isHost, myPlayerId, aiPersonas, busy, setBusy, error, setError, onExit, onLeave,
}: {
  snap: ThreeStepRoomSnapshot
  isHost: boolean
  myPlayerId: string | null
  aiPersonas: ThreeStepAiPersonaRow[]
  busy: boolean
  setBusy: (v: boolean) => void
  error: string | null
  setError: (v: string | null) => void
  /** Back button — just navigates away, room stays in lobby. */
  onExit: () => void
  /** Explicit Leave button — abandons the seat. */
  onLeave: () => Promise<void>
}) {
  const t = useT()
  // Photos for the human seats — member rows only carry display_name.
  const avatars = useProfileAvatars(snap.players.filter(p => !p.is_ai).map(p => p.profile_id))
  const { room, players } = snap
  const playerCount = players.length

  // In-room rename state — mirrors the IET LiveRoomScreen pattern.
  // Lets a guest who landed as "Player" (Apple-fallback or otherwise)
  // fix their name without leaving and rejoining.
  const myRow = players.find(p => p.id === myPlayerId) ?? null
  const myDisplayName = myRow?.display_name ?? null
  const [nameDraft, setNameDraft] = useState<string>(
    // Empty draft when the seat is the literal "Player" placeholder so
    // the user knows they need to type something, not just leave it.
    myDisplayName && myDisplayName !== 'Player' ? myDisplayName : '',
  )
  const [renaming, setRenaming] = useState(false)
  useEffect(() => {
    if (myDisplayName && myDisplayName !== 'Player') setNameDraft(myDisplayName)
  }, [myDisplayName])

  async function saveNameIfChanged() {
    const trimmed = nameDraft.trim()
    if (!trimmed || trimmed === myDisplayName) return
    setRenaming(true)
    try { await renameThreeStepSeat(room.id, trimmed) }
    catch (e) { console.warn('[3step-rename] failed', e) }
    finally { setRenaming(false) }
  }

  async function addAi(personaId: string) {
    if (!isHost || busy) return
    setBusy(true); setError(null)
    try { await addThreeStepAi(room.id, personaId) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  async function removeAi(playerId: string) {
    if (!isHost || busy) return
    setBusy(true); setError(null)
    try { await removeThreeStepAi(playerId) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  async function start() {
    if (!isHost || busy) return
    setBusy(true); setError(null)
    try { await startThreeStepGame(room.id) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  const usedPersonaIds = new Set(
    players.filter(p => p.is_ai).map(p => p.ai_player_id).filter(Boolean) as string[]
  )
  const availablePersonas = aiPersonas.filter(p => !usedPersonaIds.has(p.id))
  void myPlayerId

  return (
    <Shell title={t('Three Step Solution')} subtitle={t('Room lobby · Code {code}', { code: room.code })} onBack={onExit}>
      <div className="flex-1 flex flex-col px-5 py-4 gap-4 overflow-y-auto pb-36 relative z-10 glass-panes">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {/* Code share card */}
        <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                        rounded-2xl shadow-card p-4 text-center">
          <p className="text-[10px] font-semibold text-game-three-step-dark
                        uppercase tracking-widest mb-1">
            {t('Share this code')}
          </p>
          <p className="font-display text-4xl font-black text-brand-text tracking-[0.2em]">
            {room.code}
          </p>
          <div className="flex justify-center mt-3">
            <RoomQR code={room.code} />
          </div>
          <p className="text-xs text-brand-muted mt-2">{t('Friends scan the QR or tap "Join with code". No app to install, same room.')}</p>
          <div className="mt-3 pt-3 border-t border-game-three-step-dark/30">
            <p className="text-[10px] font-semibold text-game-three-step-dark
                          uppercase tracking-widest mb-1">
              {t('Project to TV')}
            </p>
            <p className="text-xs text-brand-muted">
              Open{' '}
              <a
                href={`https://games.jordandraper.com/tv/three-step/${room.code}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-brand-text underline decoration-brand-text/30 hover:decoration-brand-text"
              >
                games.jordandraper.com/tv/three-step/{room.code}
              </a>{' '}
              on any TV or laptop for the big-screen view.
            </p>
          </div>
        </div>

        {/* In-room rename — for guests who landed as "Player" because
            their profile didn't have a name yet (Apple Sign-In often
            doesn't share one). Updates seat + profile in one RPC. */}
        {myDisplayName !== null && (
          <div>
            <label htmlFor="3step-room-name"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Your name')}
            </label>
            <input
              id="3step-room-name"
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value.slice(0, 24))}
              onBlur={saveNameIfChanged}
              onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
              disabled={renaming}
              maxLength={24}
              placeholder={t('Type your name')}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border
                         bg-white text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-game-three-step-dark focus:ring-2
                         focus:ring-game-three-step-dark/20 transition-all disabled:opacity-60"
            />
          </div>
        )}

        {/* Players list */}
        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Players ({count}/6)', { count: playerCount })}
          </p>
          <ul className="space-y-2">
            {players.map(p => (
              <li key={p.id} className="card p-3 flex items-center gap-3">
                {p.is_ai
                  ? <span className="text-2xl shrink-0">
                      {aiPersonas.find(a => a.id === p.ai_player_id)?.avatar_emoji ?? '🤖'}
                    </span>
                  : <Avatar name={p.display_name} avatarUrl={p.profile_id ? avatars[p.profile_id] ?? null : null} size="sm" className="shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-brand-text text-sm truncate">{p.display_name}</p>
                  <p className="text-xs text-brand-muted">
                    {p.is_ai ? t('AI') : (p.profile_id === room.host_id ? t('Host') : t('Player'))}
                  </p>
                </div>
                {p.is_ai && isHost && (
                  <button
                    onClick={() => removeAi(p.id)}
                    disabled={busy}
                    className="shrink-0 px-3 py-1.5 rounded-full bg-brand-danger/10
                               text-xs font-semibold text-brand-danger
                               border border-brand-danger/30 active:scale-95"
                  >
                    {t('Remove')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Add AI (host only) */}
        {isHost && playerCount < 6 && availablePersonas.length > 0 && (
          <div>
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              {t('Add AI opponents')}
            </p>
            <ul className="space-y-2">
              {availablePersonas.map(p => (
                <li key={p.id} className="card p-3 flex items-center gap-3">
                  <span className="text-2xl shrink-0">{p.avatar_emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-brand-text text-sm">{p.name}</p>
                    <p className="text-xs text-brand-muted line-clamp-2">{t(p.persona)}</p>
                  </div>
                  <button
                    onClick={() => addAi(p.id)}
                    disabled={busy}
                    className="shrink-0 px-3 py-1.5 rounded-full bg-game-three-step-dark
                               text-xs font-semibold text-white active:scale-95"
                  >
                    {t('Add')}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="floating-actions space-y-2">
        {isHost ? (
          <button
            onClick={start}
            disabled={busy || playerCount < 2}
            className="btn-three-step
                       disabled:opacity-40"
          >
            {playerCount < 2 ? t('Need at least 2 players') : t('Start the game')}
          </button>
        ) : (
          <p className="text-center text-sm text-brand-muted">
            {t('Waiting for the host to start…')}
          </p>
        )}
        <button
          onClick={onLeave}
          disabled={busy}
          className="w-full text-center text-sm text-brand-muted py-2
                     active:text-brand-text disabled:opacity-40"
        >
          {t('Leave room')}
        </button>
      </div>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────
// Phase 1 — write 3 actions
// ────────────────────────────────────────────────────────────────────

function Phase1View({
  snap, myPlayerId, roomId, onExit,
}: {
  snap: ThreeStepRoomSnapshot
  myPlayerId: string | null
  roomId: string
  onExit: () => void
}) {
  const t = useT()
  const { players, actions, progress } = snap
  const active = players.filter(p => !p.skipped)
  // Other players' actions are RLS-hidden during phase 1, so derive the
  // live "who's submitted" set from the server-computed progress (which
  // bypasses RLS) — never from the local `actions` array, which only
  // ever holds your own row here.
  const phase1Done = new Set(progress.phase1DoneIds)
  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [c, setC] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const myActions = myPlayerId
    ? actions.filter(x => x.player_id === myPlayerId)
    : []
  const myDone = myActions.length === 3 && myActions.every(x => x.text.trim().length > 0)

  // Hydrate inputs from existing actions if the player already submitted
  // (re-entered the room mid-game).
  useEffect(() => {
    if (myActions.length === 3) {
      const sorted = myActions.slice().sort((x, y) => x.slot - y.slot)
      setA(sorted[0].text); setB(sorted[1].text); setC(sorted[2].text)
    }
  }, [myActions.length])

  const submittedCount = active.filter(p => phase1Done.has(p.id)).length

  async function submit() {
    if (!myPlayerId) return
    if (!a.trim() || !b.trim() || !c.trim()) return
    setBusy(true); setError(null)
    try { await submitThreeStepActions(roomId, a.trim(), b.trim(), c.trim()) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  if (myDone) {
    // Players (un-skipped) who haven't submitted all 3 actions yet —
    // from server progress, since others' actions are RLS-hidden here.
    const outstanding = active
      .filter(p => !phase1Done.has(p.id))
      .sort((a, b) => a.join_order - b.join_order)
    return (
      <Shell title={t('Three Step Solution')} subtitle={t('Phase 1 · Waiting for everyone else…')} code={snap.room.code} onBack={onExit}>
        <div className="flex-1 flex flex-col items-center px-6 py-8 gap-4 text-center">
          <div className="w-24 h-24 rounded-full bg-game-three-step-light
                          border-4 border-game-three-step-dark
                          flex items-center justify-center text-4xl">
            ✏️
          </div>
          <h2 className="font-display text-2xl font-black text-brand-text">
            {t('Your actions are in')}
          </h2>
          <p className="text-brand-muted text-sm max-w-xs">
            {t("{done}/{total} players submitted. The round kicks off when everyone's done.", { done: submittedCount, total: active.length })}
          </p>

          {outstanding.length > 0 && (
            <div className="w-full max-w-sm mt-4 text-left">
              <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-2">
                {t('Still writing')}
              </p>
              <ul className="space-y-2">
                {outstanding.map(p => (
                  <li key={p.id} className="card p-3 flex items-center gap-3">
                    <span className="flex-1 font-semibold text-brand-text truncate">
                      {p.display_name}
                      {p.is_ai && <span className="ml-2 text-xs text-brand-muted">{t('AI')}</span>}
                    </span>
                    {!p.is_ai && (
                      <SkipPlayerButton playerId={p.id} name={p.display_name} />
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-brand-muted mt-2">
                {t('If a player has dropped, tap Skip so the round can start.')}
              </p>
            </div>
          )}
        </div>
      </Shell>
    )
  }

  const allFilled = a.trim() && b.trim() && c.trim()
  return (
    <Shell title={t('Three Step Solution')} subtitle={t('Phase 1 · Write three actions only you can see')} code={snap.room.code} onBack={onExit}>
      <div className="flex-1 flex flex-col px-5 py-5 gap-4 overflow-y-auto pb-36 relative z-10 glass-panes">
        <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                        rounded-2xl shadow-card p-4">
          <p className="text-xs font-semibold text-game-three-step-dark
                        uppercase tracking-wide mb-2">
            {t('How it works')}
          </p>
          <p className="text-sm text-brand-text leading-relaxed">{t('Write three actions: things you do or things that happen to you. Normal, weird, or completely ridiculous. Two of these (one from each of two different players) get randomly combined into a prompt for everyone to answer.')}</p>
        </div>

        <ActionField label={t('Action 1')} value={a} onChange={setA}
                     placeholder={t('e.g. walking the dog')} />
        <ActionField label={t('Action 2')} value={b} onChange={setB}
                     placeholder={t('e.g. getting struck by lightning')} />
        <ActionField label={t('Action 3')} value={c} onChange={setC}
                     placeholder={t('e.g. proposing to a bagel')} />

        {error && <p className="text-sm text-brand-danger">{error}</p>}
      </div>
      <div className="floating-actions">
        <button
          onClick={submit}
          disabled={!allFilled || busy}
          className="btn-three-step
                     disabled:opacity-40"
        >
          {busy ? t('Submitting…') : allFilled ? t('Submit my actions') : t('Fill in all three')}
        </button>
      </div>
    </Shell>
  )
}

function ActionField({
  label, value, onChange, placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder: string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">{label}</p>
        <span className={`text-xs font-medium ${
          value.length > MAX_ACTION_LEN * 0.85 ? 'text-brand-danger' : 'text-brand-muted'
        }`}>
          {value.length}/{MAX_ACTION_LEN}
        </span>
      </div>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value.slice(0, MAX_ACTION_LEN))}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                   text-brand-text placeholder:text-brand-muted/60
                   focus:outline-hidden focus:border-game-three-step-dark
                   focus:ring-2 focus:ring-game-three-step-dark/20 transition-all"
      />
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Phase 3 — write 3 steps for this round's prompt
// ────────────────────────────────────────────────────────────────────

function Phase3View({
  snap, myPlayerId, roomId, onExit,
}: {
  snap: ThreeStepRoomSnapshot
  myPlayerId: string | null
  roomId: string
  isHost: boolean
  onExit: () => void
}) {
  const t = useT()
  const { room, players, steps, progress } = snap
  const round = room.current_round
  // Others' steps are RLS-hidden this round; use server progress for counts.
  const phase3Done = new Set(progress.phase3DoneIds)
  const [s1, setS1] = useState('')
  const [s2, setS2] = useState('')
  const [s3, setS3] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mine = myPlayerId
    ? steps.find(x => x.player_id === myPlayerId && x.round === round)
    : null
  const myDone = Boolean(mine?.submitted_at)

  useEffect(() => {
    if (mine && mine.submitted_at) {
      setS1(mine.step1_text); setS2(mine.step2_text); setS3(mine.step3_text)
    }
  }, [mine?.id, mine?.submitted_at])

  const submittedCount = players.filter(p => !p.skipped && phase3Done.has(p.id)).length
  const allFilled = s1.trim() && s2.trim() && s3.trim()

  async function submit() {
    if (!myPlayerId) return
    if (!allFilled) return
    setBusy(true); setError(null)
    try { await submitThreeStepRoundSteps(roomId, s1.trim(), s2.trim(), s3.trim()) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  if (myDone) {
    // Who hasn't submitted yet? Anyone gets a Skip button next to a
    // dropped seat. Skipped seats are excluded entirely.
    const submittedIds = phase3Done
    const active = players.filter(p => !p.skipped)
    const outstanding = active
      .filter(p => !submittedIds.has(p.id))
      .sort((a, b) => a.join_order - b.join_order)
    return (
      <Shell title={t('Three Step Solution')} subtitle={t('Round {round} · Waiting for everyone else…', { round })} code={snap.room.code} onBack={onExit}>
        <div className="flex-1 flex flex-col items-center px-6 py-8 gap-4 text-center">
          <div className="w-24 h-24 rounded-full bg-game-three-step-light
                          border-4 border-game-three-step-dark
                          flex items-center justify-center text-4xl">
            ✅
          </div>
          <h2 className="font-display text-2xl font-black text-brand-text">
            {t('Your three steps are in')}
          </h2>
          <p className="text-brand-muted text-sm max-w-xs">
            {t("{done}/{total} solutions submitted. The reveal starts when everyone's done.", { done: submittedCount, total: active.length })}
          </p>

          {outstanding.length > 0 && (
            <div className="w-full max-w-sm mt-4 text-left">
              <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-2">
                {t('Still writing')}
              </p>
              <ul className="space-y-2">
                {outstanding.map(p => (
                  <li key={p.id} className="card p-3 flex items-center gap-3">
                    <span className="flex-1 font-semibold text-brand-text truncate">
                      {p.display_name}
                      {p.is_ai && <span className="ml-2 text-xs text-brand-muted">{t('AI')}</span>}
                    </span>
                    {!p.is_ai && (
                      <SkipPlayerButton playerId={p.id} name={p.display_name} />
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-brand-muted mt-2">
                {t('If a player has dropped, tap Skip so the round can advance.')}
              </p>
            </div>
          )}
        </div>
      </Shell>
    )
  }

  return (
    <Shell title={t('Three Step Solution')} subtitle={t('Round {round} · Three steps to answer the prompt', { round })} code={snap.room.code} onBack={onExit}>
      <div className="flex-1 flex flex-col px-5 py-5 gap-4 overflow-y-auto pb-36 relative z-10 glass-panes">
        <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                        rounded-2xl shadow-card p-4">
          <p className="text-xs font-semibold text-game-three-step-dark
                        uppercase tracking-wide mb-2">
            {t('The prompt')}
          </p>
          <p className="text-lg font-semibold text-brand-text leading-snug">
            {buildPromptText(room.current_action_a ?? '', room.current_action_b ?? '', t)}
          </p>
        </div>

        <StepField label={t('Step 1')} value={s1} onChange={setS1} placeholder={t("What's the first move?")} />
        <StepField label={t('Step 2')} value={s2} onChange={setS2} placeholder={t('Then what?')} />
        <StepField label={t('Step 3')} value={s3} onChange={setS3} placeholder={t('Stick the landing.')} />

        {error && <p className="text-sm text-brand-danger">{error}</p>}
      </div>
      <div className="floating-actions">
        <button
          onClick={submit}
          disabled={!allFilled || busy}
          className="btn-three-step
                     disabled:opacity-40"
        >
          {busy ? t('Submitting…') : allFilled ? t('Submit my solution') : t('Fill in all three')}
        </button>
      </div>
    </Shell>
  )
}

function SkipPlayerButton({ playerId, name }: { playerId: string; name: string }) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function onClick() {
    if (busy) return
    if (!confirm(t("Skip {name}? They'll sit out the rest of the game (earlier votes kept) and the round will advance.", { name }))) return
    setBusy(true); setError(null)
    try { await kickThreeStepPlayer(playerId) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }
  return (
    <>
      <button
        onClick={onClick}
        disabled={busy}
        className="text-xs font-semibold text-brand-danger underline decoration-brand-danger/40
                   hover:decoration-brand-danger disabled:opacity-50"
      >
        {busy ? t('Skipping…') : t('Skip')}
      </button>
      {error && <span className="text-xs text-brand-danger">{error}</span>}
    </>
  )
}

function StepField({
  label, value, onChange, placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder: string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">{label}</p>
        <span className={`text-xs font-medium ${
          value.length > MAX_STEP_LEN * 0.85 ? 'text-brand-danger' : 'text-brand-muted'
        }`}>
          {value.length}/{MAX_STEP_LEN}
        </span>
      </div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value.slice(0, MAX_STEP_LEN))}
        rows={2}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                   text-brand-text placeholder:text-brand-muted/60 resize-none text-sm
                   focus:outline-hidden focus:border-game-three-step-dark focus:ring-2
                   focus:ring-game-three-step-dark/20 transition-all leading-relaxed"
      />
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Phase 4 reveal — walk through solutions
// ────────────────────────────────────────────────────────────────────

function RevealView({
  snap, roomId, onExit,
}: {
  snap: ThreeStepRoomSnapshot
  roomId: string
  isHost: boolean
  onExit: () => void
}) {
  const t = useT()
  const { room, players, steps } = snap
  const round = room.current_round
  const idx = room.reveal_index
  // Skipped (dropped) seats are excluded from the spotlight walk; the
  // server counts the same non-skipped set so reveal_index stays aligned.
  const ordered = players
    .filter(p => !p.skipped)
    .sort((a, b) => a.join_order - b.join_order)
  const player = ordered[idx]
  const playerSteps = player
    ? steps.find(s => s.player_id === player.id && s.round === round)
    : null
  const [busy, setBusy] = useState(false)
  // Anti-spam: after any nav press, lock both buttons for 3s so two
  // people tapping fast can't skip a spotlight or fight each other.
  const [cooldown, setCooldown] = useState(false)
  const locked = busy || cooldown
  function startCooldown() {
    setCooldown(true)
    setTimeout(() => setCooldown(false), 3000)
  }

  async function next() {
    if (locked) return
    setBusy(true); startCooldown()
    // Pass the index we're looking at so a stale double-click no-ops
    // instead of skipping a spotlight.
    try { await advanceThreeStepReveal(roomId, idx) }
    catch (e) { console.warn('[3step] advance reveal fail', e) }
    finally { setBusy(false) }
  }

  async function back() {
    if (locked || idx === 0) return
    setBusy(true); startCooldown()
    try { await stepBackThreeStepReveal(roomId, idx) }
    catch (e) { console.warn('[3step] step back reveal fail', e) }
    finally { setBusy(false) }
  }

  if (!player) return null

  return (
    <Shell
      title={t('Three Step Solution')}
      subtitle={t('Round {round} · Reveal · Solution {n} of {total}', { round, n: idx + 1, total: ordered.length })}
      code={snap.room.code}
      onBack={onExit}
    >
      <div className="flex-1 flex flex-col px-5 py-5 gap-4 overflow-y-auto pb-36 relative z-10 glass-panes">
        <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                        rounded-2xl shadow-card p-4">
          <p className="text-xs font-semibold text-game-three-step-dark
                        uppercase tracking-wide mb-2">
            {t('The prompt')}
          </p>
          <p className="text-lg font-semibold text-brand-text leading-snug">
            {buildPromptText(room.current_action_a ?? '', room.current_action_b ?? '', t)}
          </p>
        </div>

        <div className="card p-5">
          <p className="text-xs font-semibold text-game-three-step-dark
                        uppercase tracking-wide mb-3">
            {t("{name}'s solution", { name: player.display_name })}
          </p>
          <ol className="space-y-3 text-base text-brand-text">
            <li className="flex gap-3">
              <span className="font-display font-black text-game-three-step-dark
                               w-6 shrink-0">1.</span>
              <span className="leading-relaxed whitespace-pre-wrap">
                {playerSteps?.step1_text ?? '…'}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-display font-black text-game-three-step-dark
                               w-6 shrink-0">2.</span>
              <span className="leading-relaxed whitespace-pre-wrap">
                {playerSteps?.step2_text ?? '…'}
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-display font-black text-game-three-step-dark
                               w-6 shrink-0">3.</span>
              <span className="leading-relaxed whitespace-pre-wrap">
                {playerSteps?.step3_text ?? '…'}
              </span>
            </li>
          </ol>
        </div>
      </div>
      <div className="floating-actions flex gap-2">
        {/* Any player can advance — the game never waits on the host,
            who may themselves have dropped. Back recovers an accidental
            over-advance; both are locked 3s after a press (anti-spam). */}
        {idx > 0 && (
          <button
            onClick={back}
            disabled={locked}
            className="btn-three-step-outline w-auto px-5 disabled:opacity-40"
          >
            {t('← Back')}
          </button>
        )}
        <button
          onClick={next}
          disabled={locked}
          className="btn-three-step flex-1 disabled:opacity-40"
        >
          {idx >= ordered.length - 1 ? t('Start voting') : t('Next solution')}
        </button>
      </div>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────
// Phase 4 voting — pick a favorite (not your own)
// ────────────────────────────────────────────────────────────────────

function VotingView({
  snap, myPlayerId, roomId, onExit,
}: {
  snap: ThreeStepRoomSnapshot
  myPlayerId: string | null
  roomId: string
  isHost: boolean
  onExit: () => void
}) {
  const t = useT()
  const { room, players, steps, votes } = snap
  const round = room.current_round
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const myVote = myPlayerId
    ? votes.find(v => v.round === round && v.voter_id === myPlayerId)
    : null

  async function vote(targetId: string) {
    if (busy || !myPlayerId) return
    if (targetId === myPlayerId) return
    setBusy(true); setError(null)
    try { await castThreeStepRoundVote(roomId, targetId) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  // Skipped (dropped) seats don't vote and can't be voted for.
  const active = players.filter(p => !p.skipped)
  const activeIds = new Set(active.map(p => p.id))
  const totalVotes = votes.filter(v => v.round === round && activeIds.has(v.voter_id)).length
  const ordered = active.slice().sort((a, b) => a.join_order - b.join_order)
  // Voters who haven't cast yet — anyone can skip a dropped seat.
  const votedIds = new Set(votes.filter(v => v.round === round).map(v => v.voter_id))
  const outstandingVoters = ordered.filter(p => !votedIds.has(p.id))

  return (
    <Shell title={t('Three Step Solution')} subtitle={t('Round {round} · Vote · Pick your favorite (not yours)', { round })} code={snap.room.code} onBack={onExit}>
      <div className="flex-1 flex flex-col px-5 py-4 gap-3 overflow-y-auto pb-10 relative z-10 glass-panes">
        <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                        rounded-2xl shadow-card p-3">
          <p className="text-[10px] font-semibold text-game-three-step-dark
                        uppercase tracking-widest mb-1">
            {t('The prompt')}
          </p>
          <p className="text-base font-semibold text-brand-text leading-snug">
            {buildPromptText(room.current_action_a ?? '', room.current_action_b ?? '', t)}
          </p>
        </div>

        <p className="text-xs text-brand-muted">
          {t('{done}/{total} votes cast', { done: totalVotes, total: active.length })}
        </p>

        {/* List voters who haven't cast yet, with Skip buttons. Any
            player can unstick the round when someone has dropped. */}
        {outstandingVoters.length > 0 && (
          <div className="card p-3">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-2">
              {t('Still voting')}
            </p>
            <ul className="space-y-2">
              {outstandingVoters.map(p => (
                <li key={p.id} className="flex items-center gap-3">
                  <span className="flex-1 font-semibold text-brand-text truncate">
                    {p.display_name}
                    {p.is_ai && <span className="ml-2 text-xs text-brand-muted">{t('AI')}</span>}
                  </span>
                  {!p.is_ai && (
                    <SkipPlayerButton playerId={p.id} name={p.display_name} />
                  )}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-brand-muted mt-2">
              {t('Tap Skip to remove a player who has dropped.')}
            </p>
          </div>
        )}

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {ordered.map(p => {
          const isOwn = p.id === myPlayerId
          const voted = myVote?.target_id === p.id
          const playerSteps = steps.find(s => s.player_id === p.id && s.round === round)
          return (
            <button
              key={p.id}
              onClick={() => !isOwn && vote(p.id)}
              disabled={isOwn || busy}
              className={`w-full text-left rounded-2xl shadow-card p-4 border-2
                          transition-all
                          ${isOwn
                            ? 'bg-brand-elevated border-brand-border opacity-60 cursor-not-allowed'
                            : voted
                              ? 'bg-game-three-step-light border-game-three-step-dark ring-2 ring-game-three-step-dark/50'
                              : 'bg-game-three-step-light border-game-three-step-dark active:scale-[0.98]'}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-brand-text">{p.display_name}</span>
                {isOwn && (
                  <span className="text-[10px] bg-white text-game-three-step-dark
                                   px-2 py-0.5 rounded-full font-medium">
                    {t('You')}
                  </span>
                )}
                {voted && (
                  <span className="text-xs text-game-three-step-dark font-semibold">
                    {t('✓ Voted')}
                  </span>
                )}
              </div>
              <ol className="space-y-1.5 text-sm text-brand-text">
                <li className="flex gap-2">
                  <span className="font-display font-bold text-game-three-step-dark
                                   w-4 shrink-0">1.</span>
                  <span className="leading-relaxed">{playerSteps?.step1_text ?? '…'}</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-display font-bold text-game-three-step-dark
                                   w-4 shrink-0">2.</span>
                  <span className="leading-relaxed">{playerSteps?.step2_text ?? '…'}</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-display font-bold text-game-three-step-dark
                                   w-4 shrink-0">3.</span>
                  <span className="leading-relaxed">{playerSteps?.step3_text ?? '…'}</span>
                </li>
              </ol>
            </button>
          )
        })}
      </div>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────
// Between rounds — recap, then next round
// ────────────────────────────────────────────────────────────────────

function BetweenRoundsView({
  snap, roomId, onExit,
}: {
  snap: ThreeStepRoomSnapshot
  roomId: string
  isHost: boolean
  onExit: () => void
}) {
  const t = useT()
  const { room, players } = snap
  const [busy, setBusy] = useState(false)

  const ranked = players.slice().sort((a, b) => b.total_votes - a.total_votes)

  async function next() {
    if (busy) return
    setBusy(true)
    // Pass the round we're on so a stale double-click no-ops instead of
    // skipping a round.
    try { await advanceThreeStepRound(roomId, room.current_round) }
    catch (e) { console.warn('[3step] advance round fail', e) }
    finally { setBusy(false) }
  }

  return (
    <Shell
      title={t('Three Step Solution')}
      subtitle={t('Round {round} done · Round {next} of 3 next', { round: room.current_round, next: room.current_round + 1 })}
      code={snap.room.code}
      onBack={onExit}
    >
      <div className="flex-1 flex flex-col px-5 py-5 gap-4 overflow-y-auto pb-36 relative z-10 glass-panes">
        <div className="card p-4">
          <p className="text-xs font-semibold text-brand-text uppercase tracking-wide mb-3">
            {t('Standings so far')}
          </p>
          <ul className="space-y-2">
            {ranked.map((p, i) => (
              <li key={p.id} className="flex items-center gap-3">
                <span className={`font-display font-black text-2xl w-7 text-center
                  ${i === 0 ? 'text-brand-gold' : 'text-game-three-step-dark'}`}>
                  {i + 1}
                </span>
                <span className="flex-1 font-semibold text-brand-text">{p.display_name}</span>
                <span className="font-display font-bold text-game-three-step-dark">
                  {p.total_votes === 1 ? t('1 vote') : t('{n} votes', { n: p.total_votes })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="floating-actions">
        {/* Any player can advance — never blocked on the host. */}
        <button
          onClick={next}
          disabled={busy}
          className="btn-three-step
                     disabled:opacity-40"
        >
          {t('Start round {n}', { n: room.current_round + 1 })}
        </button>
      </div>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────
// Results — final leaderboard
// ────────────────────────────────────────────────────────────────────

function ResultsView({
  snap, myPlayerId, onExit,
}: {
  snap: ThreeStepRoomSnapshot
  myPlayerId: string | null
  onExit: () => void
}) {
  const t = useT()
  const { room, players, steps, actions } = snap
  const ranked = players.slice().sort((a, b) => b.total_votes - a.total_votes)
  const champ = ranked[0]
  const champVotes = champ?.total_votes ?? 0
  const tied = ranked.filter(p => p.total_votes === champVotes && champVotes > 0)
  void myPlayerId

  const roundsSeen = Array.from(new Set(steps.map(s => s.round))).sort((a, b) => a - b)

  // Round prompts come from the actions table: 20260815120000 stamps
  // each consumed action with the round + slot that used it. Rooms
  // finished before that migration have null stamps — for those, the
  // room row still holds the FINAL round's prompt, and older rounds
  // show their solutions without a prompt line.
  function promptForRound(round: number): string | null {
    const a = actions.find(x => x.used_in_round === round && x.used_as_slot === 'a')?.text
      ?? (round === room.current_round ? room.current_action_a : null)
    const b = actions.find(x => x.used_in_round === round && x.used_as_slot === 'b')?.text
      ?? (round === room.current_round ? room.current_action_b : null)
    return a && b ? buildPromptText(a, b, t) : null
  }

  return (
    <Shell title={t('Three Step Solution')} subtitle={t('Final results')} code={snap.room.code} onBack={onExit}>
      <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-36 relative z-10 glass-panes">
        {champ && champVotes > 0 ? (
          <div className="bg-game-three-step-light border-2 border-game-three-step-dark
                          rounded-2xl shadow-card p-5 text-center">
            <p className="text-xs font-semibold text-game-three-step-dark
                          uppercase tracking-widest mb-2">
              {tied.length > 1 ? t('Tied for the win') : t('Champion')}
            </p>
            <h2 className="font-display text-3xl font-black text-brand-text">
              {tied.map(t => t.display_name).join(' & ')}
            </h2>
            <p className="text-sm text-brand-muted mt-1">
              {champVotes === 1 ? t('1 vote across 3 rounds') : t('{n} votes across 3 rounds', { n: champVotes })}
            </p>
          </div>
        ) : (
          <div className="card p-5 text-center">
            <p className="font-semibold text-brand-text">{t('No clear winner')}</p>
            <p className="text-sm text-brand-muted mt-1">
              {t('Nobody got a vote. Try again with sharper solutions.')}
            </p>
          </div>
        )}

        <div className="card p-4">
          <p className="text-xs font-semibold text-brand-text uppercase tracking-wide mb-3">
            {t('Final standings')}
          </p>
          <ul className="space-y-2">
            {ranked.map((p, i) => (
              <li key={p.id} className="flex items-center gap-3">
                <span className={`font-display font-black text-2xl w-7 text-center
                  ${i === 0 ? 'text-brand-gold' : 'text-game-three-step-dark'}`}>
                  {i + 1}
                </span>
                <span className="flex-1 font-semibold text-brand-text">{p.display_name}</span>
                <span className="font-display font-bold text-game-three-step-dark">
                  {p.total_votes === 1 ? t('1 vote') : t('{n} votes', { n: p.total_votes })}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Every round's prompt + every player's three steps — the
            full story of the game, not just the vote totals. */}
        {roundsSeen.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-brand-text uppercase tracking-wide">
              {t('Every round')}
            </p>
            {roundsSeen.map(round => {
              const prompt = promptForRound(round)
              const roundSteps = steps
                .filter(s => s.round === round)
                .sort((a, b) => b.votes - a.votes)
              return (
                <div key={round} className="card p-4 space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide">
                      {t('Round {n}', { n: round })}
                    </p>
                    {prompt && (
                      <p className="font-display text-lg font-bold text-brand-text leading-snug mt-0.5">
                        {prompt}
                      </p>
                    )}
                  </div>
                  <ul className="space-y-3">
                    {roundSteps.map(s => {
                      const author = players.find(p => p.id === s.player_id)
                      return (
                        <li key={s.id} className="rounded-xl bg-brand-elevated p-3">
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <span className="font-semibold text-brand-text text-sm truncate">
                              {author?.display_name ?? '…'}
                            </span>
                            <span className="text-xs text-brand-muted shrink-0">
                              {s.votes === 1 ? t('1 vote') : t('{n} votes', { n: s.votes })}
                            </span>
                          </div>
                          <ol className="space-y-1 text-sm text-brand-text">
                            {[s.step1_text, s.step2_text, s.step3_text].map((text, i) => (
                              <li key={i} className="flex gap-1.5">
                                <span className="font-bold text-game-three-step-dark shrink-0">{i + 1}.</span>
                                <span>{text}</span>
                              </li>
                            ))}
                          </ol>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
          </div>
        )}

        <p className="text-xs text-brand-muted text-center">
          {roundsSeen.length === 1
            ? t('Played 1 round · Room {code}', { code: room.code })
            : t('Played {n} rounds · Room {code}', { n: roundsSeen.length, code: room.code })}
        </p>
      </div>
      <div className="floating-actions">
        <button
          onClick={onExit}
          className="btn-three-step"
        >
          {t('Back to lobby')}
        </button>
      </div>
    </Shell>
  )
}
